export type CloudKey =
    | 'Public'
    | 'USSec'
    | 'USNat'
    | 'Bleu'
    | 'Delos'
    | 'GovSG';

/** How build and deployment files reach the cloud. Drives the transfer-SLA label. */
export type TransferMechanism = 'CDDS' | 'CTS' | 'None';

export interface SovereignCloud {
    key: CloudKey;
    displayName: string;
    portalDeployed: boolean;
    accountFormat: string;
    accessRequirements: string[];
    transfer: TransferMechanism;
}

export const SOVEREIGN_CLOUDS: SovereignCloud[] = [
    {
        key: 'Public',
        displayName: 'Public',
        portalDeployed: true,
        accountFormat: '<alias>@microsoft.com',
        accessRequirements: ['Microsoft corporate network or VPN'],
        transfer: 'None',
    },
    {
        key: 'USSec',
        displayName: 'USSec',
        portalDeployed: true,
        accountFormat: 'RXME (USSec) account',
        accessRequirements: ['USSec Access', 'no Digital Escort required'],
        transfer: 'CTS',
    },
    {
        key: 'USNat',
        displayName: 'USNat',
        portalDeployed: true,
        accountFormat: 'EXME (USNat) account',
        accessRequirements: ['USNat Access', 'no Digital Escort required'],
        transfer: 'CTS',
    },
    {
        key: 'Bleu',
        displayName: 'Bleu',
        portalDeployed: true,
        accountFormat: '<alias>@sov.gbl.msidentity.com',
        accessRequirements: [
            'FRME (Bleu) tenant account',
            'Digital escort required for all access',
        ],
        transfer: 'CTS',
    },
    {
        key: 'Delos',
        displayName: 'Delos',
        portalDeployed: true,
        accountFormat: 'DEME (Delos) account',
        accessRequirements: [
            'Secure Admin Workstation (SAW)',
            'No escort required',
        ],
        transfer: 'CTS',
    },
    {
        key: 'GovSG',
        displayName: 'GovSG',
        portalDeployed: false,
        accountFormat: 'SGME (GovSG) account',
        accessRequirements: [
            "GovSG network access through your organization's access process",
            'No escort',
        ],
        transfer: 'CDDS',
    },
];

/** Cloud names arrive from the backend as plain strings, so match case-insensitively. */
export const getCloud = (key?: string): SovereignCloud | undefined =>
    key
        ? SOVEREIGN_CLOUDS.find(
              (cloud) => cloud.key.toUpperCase() === key.toUpperCase()
          )
        : undefined;

/**
 * How files actually reach a cloud. The SLA config key is `REACT_APP_CTS_<CLOUD>_SLA` for every
 * cloud, but GovSG uses CDDS during buildout, so user-facing labels use the mechanism from here
 * rather than the config key name.
 */
export const getTransferMechanism = (
    cloudName?: string
): TransferMechanism | undefined => getCloud(cloudName)?.transfer;
